/* eslint-disable jsx-a11y/label-has-associated-control */
import { CreatableSingleSelect } from '@ouster/webviz-react-components';

import { isNull } from 'lodash';
import { useRef, useState } from 'react';
import { RxArrowRight } from 'react-icons/rx';

import countries from '../../data/mCountries.json';
import states from '../../data/mStates.json';
import styles from '../../pages/PublicPage.module.css';
import { useStoreActions } from '../../reducers';

type Country = {
  id: number;
  name: string;
  iso2: string;
};

type State = {
  id: number;
  name: string;
  country_id: number;
};

const mappedCountries = countries.map((country: Country) => ({
  value: country.name,
  label: country.name,
  data: country,
}));

const mappedState = states.map((state: State) => ({
  value: state.name,
  label: state.name,
  data: state,
}));

type UserLocationProps = {
  updateUserLocation: (country: string, state: string) => void;
};

export default function UserLocation({
  updateUserLocation,
}: UserLocationProps) {
  const { validateArea } = useStoreActions((actions) => actions.user);

  const [userCountry, setUserCountry] = useState('');
  const [userState, setUserState] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const countryName = useRef('');
  const stateName = useRef('');

  const updateUserLocationHandler = () => {
    const validateUserArea = async () => {
      try {
        await validateArea({
          country: countryName.current,
          state: stateName.current,
        });
        updateUserLocation(countryName.current, stateName.current);
      } catch (error) {
        if (error instanceof Error) {
          setErrorMessage(error.message);
        }
      }
    };
    validateUserArea();
  };

  const countryStates = mappedState.filter(
    (state) => state.data.country_id.toString() === userCountry
  );

  const handleCountryChange = (item: string | Country | null) => {
    setUserState('');

    if (isNull(item)) {
      setUserCountry('');
      return;
    }
    if (typeof item !== 'string') {
      setUserCountry(item.id.toString());
      countryName.current = item.name;
    }
  };

  const handleStateChange = (item: string | State | null) => {
    if (isNull(item)) {
      setUserState('');
      return;
    }
    if (typeof item !== 'string') {
      setUserState(item.id.toString());
      stateName.current = item.name;
    }
  };

  return (
    <div className={styles['auth-content']} data-testid="user-location-section">
      {/* text copy on left */}
      <div className={styles['auth-copy-container']}>
        <h2 className={styles['auth-page-title']}>Tell us where you’re from</h2>
        <p className={styles['auth-page-copy']}>
          Location helps us personalize your experience.
        </p>
      </div>

      <div className={styles['auth-form-container']} style={{ gap: '3rem' }} data-testid="user-location-form">
        {errorMessage !== '' && (
          <div className={styles['error-message-with-link']}>
            {errorMessage}
          </div>
        )}
        <div>
          <label
            htmlFor="country"
            style={{ marginBottom: '1rem', display: 'block' }}
          >
            Country
          </label>
          <CreatableSingleSelect
            placeholderText="Select your country"
            menuPlacement="bottom"
            options={mappedCountries}
            onOptionChange={handleCountryChange}
            size="lg"
            data-testid="user-location-country"
          />
        </div>
        <div>
          <label
            htmlFor="state"
            style={{ marginBottom: '1rem', display: 'block' }}
          >
            State / Province
          </label>

          <CreatableSingleSelect
            placeholderText={
              countryStates.length > 0
                ? 'Select your state / province'
                : 'No option'
            }
            menuPlacement="bottom"
            isDisabled={countryStates.length === 0}
            options={countryStates}
            onOptionChange={handleStateChange}
            key={userCountry}
            size="lg"
            data-testid="user-location-state"
          />
        </div>
        <button
          type="button"
          className={styles['primary-button']}
          style={{ marginTop: '4rem' }}
          disabled={!userCountry || (!userState && countryStates.length > 0)}
          onClick={updateUserLocationHandler}
          data-testid="user-location-continue"
        >
          Continue
          <RxArrowRight />
        </button>
      </div>
    </div>
  );
}
